import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/core/resource/ResourcesGroupName";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {SlowMo, TimelineLite, TweenLite, TweenMax} from "gsap";
import {TeslaSymbols} from "../../config/TeslaSymbols";
import {Tesla} from "../Tesla";
import {TeslaGameAssets} from "../TeslaGameAssets";
import {TeslaGameConfig} from "../TeslaGameConfig";
import {TeslaSoundConfig} from "../TeslaSoundConfig";
import {MathHelper} from "../utils/MathHelper";

/**
 * Created by h3 on 2017-11-16.
 */

export interface IRadialPos {
    [index:number]:number;
}


export class TeslaWildCollectReleaseAnimation extends BaseView {
    private _layer:ScalableLayer;
    private _container:PIXI.Container;

    private readonly _DESKTOP_X_POS:IRadialPos = [
        473,
        614,
        745
    ];

    private readonly _MOBILE_PORTRAIT_X_POS:IRadialPos = [
        194,
        334,
        468
    ];


    private _radialXPos:IRadialPos;
    private _radialYPos:number;
    private _dischargeWild_y_start:number;

    constructor(resourcesGroup:ResourcesGroupName) {
        super(resourcesGroup);
        this._layer = StageManager.getLayer(TeslaGameConfig.instance.LAYERS.SYMBOLS.name);
    }


    protected initAnimations():void {
        this._container = new PIXI.Container();
        this._layer.addChild(this._container);
    }


    protected onResize(resizeData:IResizeData):void {
        this._radialXPos = this._DESKTOP_X_POS;
        this._radialYPos = 467;
        this._dischargeWild_y_start = 540;
    }

    private createGlareAnimation(reel:number, row:number):TimelineSprite {
        const radialGlare = new TimelineSprite(GameResources.getTextures(TeslaGameAssets.RADIAL_GLARE));
        radialGlare.position.set(this._radialXPos[reel] + (row * 31), this._radialYPos);
        radialGlare.anchor.set(0.5, 0.5);
        radialGlare.scale.set(0.3, 0.3);
        radialGlare.blendMode = PIXI.BLEND_MODES.ADD;
        radialGlare.hide();
        this._container.addChild(radialGlare);

        return radialGlare;
    }

    private createGlareAnimationLanding(points:PIXI.Point):TimelineSprite {
        const radialGlare = new TimelineSprite(GameResources.getTextures(TeslaGameAssets.RADIAL_GLARE));
        radialGlare.position.set(points.x, points.y);
        radialGlare.anchor.set(0.5, 0.5);
        radialGlare.scale.set(1, 1);
        radialGlare.blendMode = PIXI.BLEND_MODES.ADD;
        radialGlare.hide();
        this._container.addChild(radialGlare);

        return radialGlare;
    }


    private createCircleAnimation(points:PIXI.Point):TimelineSprite {
        const circleEffect = new TimelineSprite(GameResources.getTextures(TeslaGameAssets.SELECTION_CIRCLE_EFFECT));
        circleEffect.position.set(points.x, points.y);
        circleEffect.anchor.set(0.5, 0.5);
        circleEffect.blendMode = PIXI.BLEND_MODES.ADD;
        circleEffect.hide();
        this._container.addChild(circleEffect);

        return circleEffect;
    }

    private createDischargeWild():PIXI.Sprite {
        const dischargeWild = new PIXI.Sprite(GameResources.getTextures(TeslaGameAssets.WILD_DISCHARGE)[0]);
        dischargeWild.anchor.set(0.5, 0.5);
        dischargeWild.scale.set(0, 0);
        this._container.addChild(dischargeWild);

        return dischargeWild;
    }


    private addWildSymbol(points:PIXI.Point):TimelineSprite {
        const wildSymbol = new TimelineSprite(GameResources.getTextures("W2_attention"));
        wildSymbol.position.set(points.x, points.y);
        wildSymbol.anchor.set(0.5, 0.5);
        wildSymbol.hide();
        this._container.addChild(wildSymbol);

        return wildSymbol;

    }

    private createLightningAnimation(reel:number, row:number, points:PIXI.Point):TimelineSprite {

        const visualHeight = 308;

        const lightPosX = this._radialXPos[reel] + (row * 31);
        const lightPosY = this._radialYPos;
        const lightPoints = new PIXI.Point();
        lightPoints.set(lightPosX, lightPosY);

        let angle = MathHelper.angleBetweenPoints(points, lightPoints);
        let distance = MathHelper.distanceBetweenPoints(points, lightPoints);

        const lightningEffect = new TimelineSprite(GameResources.getTextures(TeslaGameAssets.PURPLE_LIGHTNING));
        lightningEffect.height = distance * (lightningEffect.height / visualHeight);
        lightningEffect.position.set(lightPosX, lightPosY);
        lightningEffect.hide();
        lightningEffect.anchor.set(0.649, 0.07);
        lightningEffect.blendMode = PIXI.BLEND_MODES.ADD;
        lightningEffect.rotation = angle + (90 * PIXI.DEG_TO_RAD);
        this._container.addChild(lightningEffect);

        return lightningEffect;
    }


    public getWinAnimation(points:PIXI.Point[][], symbols:StateReelSymbol[][]):TimelineLite[] {

        const totalTimeline:TimelineLite[] = [];

        for(let i:number = 0; i < 4; i++) {
            for(let j:number = 0; j < points[i].length; j++) {

                const tl:TimelineLite = new TimelineLite();

                let circleEffect = this.createCircleAnimation(points[i][j]);
                let glareAnimation = this.createGlareAnimation(i - 1, j);
                let lightningEffect = this.createLightningAnimation(i - 1, j, points[i][j]);

                if(points[i][j]) {
                    tl.add([
                        lightningEffect.getAnimationAutoShowHide(true, true),
                        circleEffect.getAnimationAutoShowHide(true, true),
                        glareAnimation.getAnimationAutoShowHide(true, false, [0, 9]),
                        new TweenLite(glareAnimation.position, 0.2, {x : points[i][j].x, y : points[i][j].y}),
                        new TweenLite(glareAnimation.scale, 0.2, {x : 1, y : 1})
                    ]);
                    tl.add(() => glareAnimation.hide(), "-= 0.05");
                    tl.add([
                        () => this._container.removeChild(glareAnimation),
                        () => this._container.removeChild(circleEffect),
                        () => this._container.removeChild(lightningEffect),
                    ]);

                    tl.add([
                        TweenMax.fromTo(symbols[i][j].scale, 0.2, {x : 1.3, y : 1.3}, {x : 1, y : 1, yoyo : true}),
                        () => {
                            symbols[i][j].replaceWith(TeslaSymbols.W2, symbols[i][j].reelId);
                        }
                    ]);
                }

                totalTimeline.push(tl);

            }
        }

        return totalTimeline;
    }


    public getWildBounceAnimation(reel:number, points:PIXI.Point[], symbols:StateReelSymbol[]):TimelineLite {

        const totalTimeline:TimelineLite = new TimelineLite();


        for(let j:number = 0; j < points.length; j++) {

            const tl:TimelineLite = new TimelineLite();

            let circleEffect = this.createCircleAnimation(points[j]);
            let glareAnimation = this.createGlareAnimationLanding(points[j]);
            let lightningEffect = this.createLightningAnimation(reel - 1, j, points[j]);

            if(points[j]) {
                tl.add([
                    lightningEffect.getAnimationAutoShowHide(true, true),
                    circleEffect.getAnimationAutoShowHide(true, true),
                    glareAnimation.getAnimationAutoShowHide(true, false),
                    new TweenLite(glareAnimation.position, 0.2, {
                        x : this._radialXPos[reel - 1] + (j * 31),
                        y : this._radialYPos
                    }),
                    new TweenLite(glareAnimation.scale, 0.2, {x : 0.3, y : 0.3})
                ]);

                tl.add(() => glareAnimation.hide(), "-= 0.05");

                tl.add([
                    () => this._container.removeChild(glareAnimation),
                    () => this._container.removeChild(circleEffect),
                    () => this._container.removeChild(lightningEffect),
                ]);

                tl.add(
                    symbols[j].changeState({
                        state : TeslaGameConfig.instance.SYMBOL_STATES.attention,
                        fadeInDuration : 0
                    })
                );
            }

            totalTimeline.add(tl);

        }

        return totalTimeline;
    }


    public getPreSpinStopAnimation(overlayWildsBeforeSpinStop:boolean[][] = []):TimelineLite[] {
        const totalTimeline:TimelineLite[] = [];

        for(let i:number = 0; i < overlayWildsBeforeSpinStop.length; i++) {

            for(let j:number = 0; j < 3; j++) {

                if(overlayWildsBeforeSpinStop[i][j] === true) {
                    const basePosition:number[] = Helper.getSymbolPositions(i, j);
                    const points:PIXI.Point = new PIXI.Point(basePosition[0], basePosition[1]);
                    const tl:TimelineLite = new TimelineLite();

                    let circleEffect = this.createCircleAnimation(points);
                    let wildSymbol = this.addWildSymbol(points);
                    let glareAnimation = this.createGlareAnimation(i - 1, j);
                    let lightningEffect = this.createLightningAnimation(i - 1, j, points);

                    if(points) {
                        tl.add([
                            lightningEffect.getAnimationAutoShowHide(true, true),
                            circleEffect.getAnimationAutoShowHide(true, true),
                            glareAnimation.getAnimationAutoShowHide(true, true, [0, 9]),
                            new TweenLite(glareAnimation.position, 0.2, {x : points.x, y : points.y}),
                            new TweenLite(glareAnimation.scale, 0.2, {x : 1, y : 1})
                        ]);
                        tl.add([
                            () => this._container.removeChild(glareAnimation),
                            () => this._container.removeChild(circleEffect),
                            () => this._container.removeChild(lightningEffect),
                        ]);
                        tl.add(wildSymbol.getAnimationAutoShowHide(true, false));

                    }

                    totalTimeline.push(tl);
                }
            }
        }

        return totalTimeline;
    }

    public getDischargeWildAnimation():TimelineLite {

        let dischargeWild = this.createDischargeWild();
        dischargeWild.position.set(TeslaGameConfig.instance.GAME_WIDTH * 0.5, 0);

        const tl:TimelineLite = new TimelineLite();

        tl.add([
            Tesla.background.getBlinkAnimation(),
            new TweenLite(dischargeWild.position, 1.5, {
                y : this._dischargeWild_y_start,
                ease : SlowMo.ease.config(0.7, 0.7, false)
            }),
            new TweenLite(dischargeWild.scale, 1.5, {x : 1.5, y : 1.5, ease : SlowMo.ease.config(0.9, 1, true)}),
            () => SlotGame.sound.playEffect(TeslaSoundConfig.instance.FS_INTRO_COIL_LIGHTNING)
        ]);
        tl.add([
            () => this._container.removeChild(dischargeWild)
        ]);

        return tl;
    }

    public removeAddedWildSymbols() {
        this._container.removeChildren()
    }

}